import {
  WSButton,
  WSFlexBox,
  WSIcon,
  WSLoader,
  WSPanel,
  WSProgressBar,
  WSText
} from "@wingspanhq/fe-component-library";
import { BulkStatus } from "@wingspanhq/payments/dist/interfaces";
import { IBulkCalculation1099ItemCreate } from "@wingspanhq/payments/dist/interfaces/api/bulkCalculation1099";
import { IBulkCalculation1099Batch } from "@wingspanhq/payments/dist/interfaces/bulkCalculation1099";
import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import { SetupFlowRouteComponentProps } from "../../components/SetupFlow/SetupFlow";
import { useUpdateBulkCalculate1099Batch } from "../../query/payments/mutations";
import {
  useBulkCalculate1099Batch,
  useCollaboratorsQuery
} from "../../query/payments/queries";
import { paymentsService } from "../../services/payments";
import { bulkActionFactory } from "../../utils/bulkActionFactory";
import { NEC1099FilingContainer } from "../components/NEC1099FilingContainer";
import { getCurrentFilingYear } from "../utils";
import { useUserId } from "../../query/hooks/helpers";

import styles from "./styles.module.scss";

interface IFailedBulkCalculation1099ItemCreate
  extends IBulkCalculation1099ItemCreate {
  error: string;
}

export const GenerateForms: React.FC<SetupFlowRouteComponentProps> = ({
  onNext
}) => {
  const userId = useUserId();
  const [areItemsAddedToBatch, setAreItemsAddedToBatch] = useState(false);
  const [isUploadingBatchItems, setIsUploadingBatchItems] = useState(false);
  const [isFormGenerationStarted, setIsFormGenerationStarted] = useState(false);

  const [isBatchFetchingFailed, setIsBatchFetchingFailed] = useState(false);
  const [totalForms, setTotalForms] = useState<number>(1);
  const [failedBatchItems, setFailedBatchItems] = useState<
    IFailedBulkCalculation1099ItemCreate[]
  >([]);
  const match = useRouteMatch<{ bulkCalculation1099BatchId: string }>();
  const bulkCalculation1099BatchId = match.params.bulkCalculation1099BatchId;

  const qCollaborators = useCollaboratorsQuery();
  const bulkCalculation1099BatchQuery = useBulkCalculate1099Batch(
    bulkCalculation1099BatchId,
    {
      enabled: isFormGenerationStarted && !isBatchFetchingFailed,
      refetchInterval: 3000
    }
  );

  // const bulkPayableBatchItemsNotYetProcessedQuery = useBulkPayableBatchItemsNotYetProcessed(
  //   bulkPayableBatchId,
  //   {
  //     refetchInterval: 3000
  //   }
  // );

  const [updateBulkCalculate1099Batch] = useUpdateBulkCalculate1099Batch();

  const bulkCreateBatchItemCalc1099 = bulkActionFactory({
    onStart: () => {
      setIsUploadingBatchItems(true);
    },
    action: async (data: IBulkCalculation1099ItemCreate) => {
      try {
        return await paymentsService.collaborator.nec1099.bulkCalculation1099BatchItem.create(
          bulkCalculation1099BatchId,
          data
        );
      } catch (error) {
        setFailedBatchItems(failedBatchItems => [
          ...failedBatchItems,
          {
            ...data,
            error:
              (error as AxiosError)?.response?.data.error ||
              "Internal server error"
          }
        ]);
        // TODO(Anesh): how shall we show the failed batch items?
      }
    },
    onFinish: async () => {
      setAreItemsAddedToBatch(true);
      setIsUploadingBatchItems(false);

      await updateBulkCalculate1099Batch(
        {
          id: bulkCalculation1099BatchId,
          data: {
            status: BulkStatus.Pending
          }
        },
        {
          onSuccess: () => {
            setIsFormGenerationStarted(true);
          }
        }
      );
    }
  });

  const bulkCalculation1099Batch = bulkCalculation1099BatchQuery.data as IBulkCalculation1099Batch;
  const isGenerationCompleted = bulkCalculation1099Batch
    ? [BulkStatus.Complete, BulkStatus.Failed].includes(
        bulkCalculation1099Batch?.status
      )
    : false;

  useEffect(() => {
    // stop polling if get batch fails
    if (bulkCalculation1099BatchQuery.isError) {
      setIsBatchFetchingFailed(true);
    }
    if (isGenerationCompleted) {
      setIsFormGenerationStarted(false);
    }
  }, [isGenerationCompleted, bulkCalculation1099BatchQuery.isError]);

  // add collaborators to batch to calculate 1099 balances
  useEffect(() => {
    let batchItemsRequest: IBulkCalculation1099ItemCreate[] = [];
    batchItemsRequest.push({
      clientId: userId,
      year: getCurrentFilingYear()
    });
    const addCollaboratorsToBatch = async (
      batchItemsRequest: IBulkCalculation1099ItemCreate[]
    ) => {
      await bulkCreateBatchItemCalc1099.run(batchItemsRequest);
    };
    if (!areItemsAddedToBatch) {
      addCollaboratorsToBatch(batchItemsRequest);
    }
  }, [qCollaborators.isSuccess]);

  useEffect(() => {
    const getCollaboratorListSize = async () => {
      const listSize = await paymentsService.collaborator.listSize();
      setTotalForms(listSize);
    };
    getCollaboratorListSize();
  }, []);

  const totalGenerated =
    bulkCalculation1099Batch?.statistics?.itemsProcessed ?? 0;
  const totalFailed = bulkCalculation1099Batch?.statistics?.itemsFailed ?? 0;
  const importPercent = (totalGenerated * 100) / totalForms;

  let progressText = "";
  if (qCollaborators.isLoading) {
    progressText = "Fetching collaborators to create 1099-NEC form...";
  } else if (isUploadingBatchItems) {
    progressText = `Creating forms..`;
  } else if (isFormGenerationStarted) {
    progressText = `Generating ${totalForms} forms`;
  } else if (isGenerationCompleted && totalFailed > 0) {
    // could not generate form for failed items
    progressText = `Could not generate ${totalFailed} forms`;
  } else if (isGenerationCompleted) {
    progressText = `${totalForms} forms generated.`;
  } else {
    progressText = "Hold on! We are generating 1099-NEC forms...";
  }
  return (
    <NEC1099FilingContainer title="Generate 1099-NEC Forms">
      <WSPanel>
        <WSText.Heading3 mb="L">
          Wingspan is generating your 1099-NEC forms.
        </WSText.Heading3>

        <WSFlexBox.CenterY>
          {isGenerationCompleted ? (
            <WSIcon name="receipt" size="M" color="blue400" />
          ) : (
            <WSLoader.Spinner />
          )}
          <WSText ml="S" color="grey700">
            {progressText}
          </WSText>
        </WSFlexBox.CenterY>
        {/* <WSProgressBar mb="M" percent={importPercent || 0} text={progressText} /> */}

        <WSButton.Primary
          mt="L"
          className={styles.actionBtn}
          fullWidth
          name="continueToNECFilingDashboard"
          onClick={onNext}
          disabled={!isGenerationCompleted}
        >
          Continue to 1099-NEC Filing Dashboard
        </WSButton.Primary>
      </WSPanel>
    </NEC1099FilingContainer>
  );
};
